import React, { useRef, useState } from 'react';
import styled from '@emotion/native';
import { LayoutChangeEvent } from 'react-native';
import { Video, ResizeMode, VideoReadyForDisplayEvent } from 'expo-av';
import { BodyText, H2 } from '@/shared/components/StyledText';
import { darkGreen, mint } from '@/shared/styles/colors';
import {
  FONT_STYLES,
  WELCOME_PROMO_VIDEO_MAX_WIDTH,
  WELCOME_PROMO_VIDEO_URL,
  WELCOME_SECTION_BODY_COPY,
  WELCOME_SECTION_GAP_NARROW,
  WELCOME_SECTION_GAP_WIDE,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
  WELCOME_SECTION_VERTICAL_BASE,
  spacing,
} from '@/shared/styles/constants';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import { ABOUT_COPY } from '@/shared/constants/aboutCopy';

/** Title → body in the About column; wide matches other welcome sections. */
const ABOUT_TITLE_TO_BODY_GAP_WIDE = 20;
const ABOUT_TITLE_TO_BODY_GAP_NARROW = 12;

const welcomePromoVideoSource = { uri: WELCOME_PROMO_VIDEO_URL };

interface WelcomePromoLocalVideoProps {
  isReduceMotionEnabled: boolean;
}

const WelcomePromoLocalVideo = ({
  isReduceMotionEnabled,
}: WelcomePromoLocalVideoProps) => {
  const videoRef = useRef<Video | null>(null);
  const [bandWidth, setBandWidth] = useState(0);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const onBandLayout = (event: LayoutChangeEvent) => {
    const nextWidth = event.nativeEvent.layout.width;
    if (nextWidth > 0 && nextWidth !== bandWidth) {
      setBandWidth(nextWidth);
    }
  };

  const onReadyForDisplay = (event: VideoReadyForDisplayEvent) => {
    if (!event.naturalSize) {
      return;
    }
    const ns = event.naturalSize;
    let nativeW = ns.width;
    let nativeH = ns.height;
    if (ns.orientation === 'portrait' && nativeW > nativeH) {
      nativeW = ns.height;
      nativeH = ns.width;
    }
    if (nativeW > 0 && nativeH > 0) {
      setNaturalSize({ width: nativeW, height: nativeH });
    }
  };

  let videoHeight = 300;
  if (bandWidth > 0 && naturalSize) {
    videoHeight = (bandWidth * naturalSize.height) / naturalSize.width;
  } else if (bandWidth > 0) {
    videoHeight = (bandWidth * 9) / 16;
  }

  return (
    <VideoBand onLayout={onBandLayout}>
      <Video
        ref={videoRef}
        source={welcomePromoVideoSource}
        style={{
          width: '100%',
          height: videoHeight,
        }}
        resizeMode={ResizeMode.CONTAIN}
        isLooping
        shouldPlay={!isReduceMotionEnabled}
        isMuted
        useNativeControls
        onReadyForDisplay={onReadyForDisplay}
        accessibilityLabel="Welcome video: short intro to virtual styling with EMME"
      />
    </VideoBand>
  );
};

interface LayoutProps {
  isWideLayout: boolean;
}

interface CopyAlignProps {
  copyAlignLeft: boolean;
}

const WelcomeVideoSection = () => {
  const isReduceMotionEnabled = usePrefersReducedMotion();
  const { isWide: isWideLayout } = useWelcomeSectionLayout();
  const shouldLeftAlignAboutHeadline = isWideLayout;

  return (
    <Section isWideLayout={isWideLayout}>
      <SectionInner isWideLayout={isWideLayout}>
        <WelcomePromoLocalVideo isReduceMotionEnabled={isReduceMotionEnabled} />
        <CopyColumn
          isWideLayout={isWideLayout}
          copyAlignLeft={shouldLeftAlignAboutHeadline}
        >
          <AboutTitleRow>
            <AboutTitleText copyAlignLeft={shouldLeftAlignAboutHeadline}>
              {ABOUT_COPY.title}
            </AboutTitleText>
          </AboutTitleRow>
          <AboutBodyStack copyAlignLeft={shouldLeftAlignAboutHeadline}>
            {ABOUT_COPY.paragraphs.map((text) => (
              <AboutBodyParagraph
                key={text}
                copyAlignLeft={shouldLeftAlignAboutHeadline}
              >
                {text}
              </AboutBodyParagraph>
            ))}
          </AboutBodyStack>
        </CopyColumn>
      </SectionInner>
    </Section>
  );
};

const Section = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  backgroundColor: mint,
  paddingTop: isWideLayout ? 100 : WELCOME_SECTION_VERTICAL_BASE,
  paddingBottom: isWideLayout ? 100 : WELCOME_SECTION_VERTICAL_BASE,
  paddingHorizontal: isWideLayout
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  alignItems: 'center',
}));

const SectionInner = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  maxWidth: isWideLayout ? WELCOME_SECTION_MAX_WIDTH : undefined,
  flexDirection: isWideLayout ? 'row' : 'column',
  alignItems: 'center',
  justifyContent: isWideLayout ? 'center' : 'flex-start',
  gap: isWideLayout ? WELCOME_SECTION_GAP_WIDE : spacing.md,
}));

interface CopyColumnProps extends LayoutProps {
  copyAlignLeft: boolean;
}

const CopyColumn = styled.View<CopyColumnProps>(
  ({ isWideLayout, copyAlignLeft }) => ({
    flexShrink: isWideLayout ? 1 : undefined,
    minWidth: 0,
    maxWidth: isWideLayout ? 520 : undefined,
    width: '100%',
    alignItems: copyAlignLeft ? 'flex-start' : 'center',
    gap: isWideLayout
      ? ABOUT_TITLE_TO_BODY_GAP_WIDE
      : ABOUT_TITLE_TO_BODY_GAP_NARROW,
  }),
);

const AboutTitleRow = styled.View({
  width: '100%',
  alignSelf: 'stretch',
});

const AboutBodyStack = styled.View<{ copyAlignLeft: boolean }>(
  ({ copyAlignLeft }) => ({
    width: '100%',
    alignItems: copyAlignLeft ? 'flex-start' : 'center',
    gap: WELCOME_SECTION_GAP_NARROW,
  }),
);

const AboutTitleText = styled(H2)<CopyAlignProps>(({ copyAlignLeft }) => ({
  ...FONT_STYLES.lora,
  color: darkGreen,
  fontSize: 40,
  lineHeight: 40.6,
  fontWeight: '400',
  textAlign: copyAlignLeft ? 'left' : 'center',
  textTransform: 'uppercase',
  letterSpacing: 1,
  width: '100%',
}));

const AboutBodyParagraph = styled(BodyText)<CopyAlignProps>(
  ({ copyAlignLeft }) => ({
    color: darkGreen,
    ...FONT_STYLES.welcomeHelveticaNeue,
    fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
    fontWeight: '300',
    letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
    lineHeight: 24,
    textAlign: copyAlignLeft ? 'left' : 'center',
    marginBottom: 0,
    width: '100%',
  }),
);

const VideoBand = styled.View({
  width: '100%',
  maxWidth: WELCOME_PROMO_VIDEO_MAX_WIDTH,
  alignSelf: 'center',
  borderRadius: 14,
  overflow: 'hidden',
  backgroundColor: mint,
  flexShrink: 0,
});

export default WelcomeVideoSection;
